import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getFirebaseApp } from '../config/firebase';

let analytics = null;
let measurementId = null;
let isInitialized = false;

// Custom event queue for events that occur before initialization
const eventQueue = [];

const setCookie = (name, value, options = {}) => {
  // Get the base domain (e.g., example.com from subdomain.example.com)
  const getBaseDomain = () => {
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length > 2) {
      return hostParts.slice(-2).join('.');
    }
    return window.location.hostname;
  };

  const domain = window.location.hostname === 'localhost' 
    ? 'localhost' 
    : getBaseDomain();
  
  const secureFlag = window.location.protocol === 'https:' ? 'Secure;' : '';
  const cookieString = `${name}=${value};path=/;SameSite=Lax;${secureFlag}max-age=63072000;domain=${domain}`;
  document.cookie = cookieString;
};

/**
 * Initialize Google Analytics 4
 * @param {string} id - Google Analytics Measurement ID (e.g., 'G-XXXXXXXXXX')
 * @returns {Object|null} - Analytics instance or null if initialization fails
 */
export const initializeAnalytics = (id) => {
  try {
    if (isInitialized && analytics) {
      console.log('Analytics already initialized');
      return analytics;
    }

    if (!id) {
      console.warn('Google Analytics Measurement ID not provided');
      return null;
    }

    measurementId = id;

    // Get the Firebase app instance
    const app = getFirebaseApp();
    if (!app) {
      console.warn('Firebase app not initialized');
      return null;
    }

    // Set initial cookies with proper attributes
    setCookie('_ga', 'GA1.1.' + Math.random().toString(36).substring(2));
    setCookie('_ga_' + id.replace(/^G-/, ''), 'GS1.1.0.0.0');

    // Initialize Analytics with custom domain config
    analytics = getAnalytics(app);

    // Check if gtag script already exists (from index.html) or add it dynamically
    if (!document.querySelector(`script[src*="${id}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(script);
    }

    // Ensure dataLayer and gtag function exist (may already be set by index.html)
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'undefined') {
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
    }

    // Configure gtag with the correct domain
    window.gtag('config', id, {
      cookie_domain: window.location.hostname === 'localhost' ? 'localhost' : '.' + window.location.hostname,
      cookie_expires: 63072000,
      cookie_update: true,
      cookie_flags: 'SameSite=Lax',
      send_page_view: true,
      anonymize_ip: true
    });

    // Override default cookie behavior
    window['ga-disable-' + id] = false;
    
    isInitialized = true;
    console.log('✅ Google Analytics initialized successfully');

    // Process queued events
    if (eventQueue.length > 0) {
      console.log(`Processing ${eventQueue.length} queued analytics events`);
      eventQueue.forEach(({ eventName, eventParams }) => {
        trackEvent(eventName, eventParams);
      });
      eventQueue.length = 0;
    }
    
    return analytics;
  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
    return null;
  }
};

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Queue event if analytics not initialized yet
  if (!isInitialized || !analytics) {
    eventQueue.push({ eventName, eventParams });
    return;
  }

  try {
    // Add common metadata to all events
    const enrichedParams = {
      ...eventParams,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
    };

    logEvent(analytics, eventName, enrichedParams);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', eventName, enrichedParams);
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Track page view
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath = window.location.pathname, pageTitle = document.title) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href,
  });
};

/**
 * Set user ID for analytics
 * @param {string} userId - User ID to track
 */
export const setAnalyticsUserId = (userId) => {
  if (!isInitialized || !analytics) {
    return;
  }

  try {
    setUserId(analytics, userId);
    console.log('✅ Analytics user ID set:', userId);
  } catch (error) {
    console.error('Error setting analytics user ID:', error);
  }
};

/**
 * Set user properties for analytics
 * @param {Object} properties - User properties (e.g., { role: 'admin', company: 'demo' })
 */
export const setAnalyticsUserProperties = (properties) => {
  if (!isInitialized || !analytics) {
    return;
  }

  try {
    setUserProperties(analytics, properties);
    console.log('✅ Analytics user properties set:', properties);
  } catch (error) {
    console.error('Error setting analytics user properties:', error);
  }
};

/**
 * Track user login
 * @param {string} userId - User ID
 * @param {string} method - Login method (e.g., 'email_password', 'google')
 */
export const trackLogin = (userId, method = 'email_password') => {
  setAnalyticsUserId(userId);
  trackEvent('login', {
    method,
    user_id: userId,
  });
};

/**
 * Track user logout
 */
export const trackLogout = () => {
  trackEvent('logout');
  setAnalyticsUserId(null);
};

/**
 * Track business events (integrates with audit logs)
 * @param {string} eventType - Type of business event
 * @param {Object} details - Event details
 */
export const trackBusinessEvent = (eventType, details = {}) => {
  trackEvent('business_event', {
    event_type: eventType,
    ...details,
  });
};

/**
 * Track property management events
 */
export const trackPropertyEvent = (action, propertyId, details = {}) => {
  trackEvent('property_action', {
    action,
    property_id: propertyId,
    ...details,
  });
};

/**
 * Track payment events
 */
export const trackPaymentEvent = (action, paymentId, amount, details = {}) => {
  trackEvent('payment_action', {
    action,
    payment_id: paymentId,
    value: amount,
    currency: 'GBP',
    ...details,
  });
};

/**
 * Track maintenance events
 */
export const trackMaintenanceEvent = (action, maintenanceId, details = {}) => {
  trackEvent('maintenance_action', {
    action,
    maintenance_id: maintenanceId,
    ...details,
  });
};

/**
 * Track tenant events
 */
export const trackTenantEvent = (action, tenantId, details = {}) => {
  trackEvent('tenant_action', {
    action,
    tenant_id: tenantId,
    ...details,
  });
};

/**
 * Track document events
 */
export const trackDocumentEvent = (action, documentId, documentType, details = {}) => {
  trackEvent('document_action', {
    action,
    document_id: documentId,
    document_type: documentType,
    ...details,
  });
};

/**
 * Track search events
 */
export const trackSearch = (searchTerm, resultsCount, category = null) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    category,
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName, success = true, errorMessage = null) => {
  trackEvent('form_submit', {
    form_name: formName,
    success,
    error_message: errorMessage,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, location = null) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location,
  });
};

/**
 * Disable analytics tracking
 */
export const disableAnalytics = () => {
  if (measurementId) {
    window['ga-disable-' + measurementId] = true;
  }
  
  // Clear existing cookies
  document.cookie = '_ga=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  if (measurementId) {
    document.cookie = `_ga_${measurementId.replace(/^G-/, '')}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }
  
  if (window.dataLayer) {
    window.dataLayer.push(['config', null, {
      'send_page_view': false
    }]);
  }
  
  analytics = null;
  isInitialized = false;
  measurementId = null;
  eventQueue.length = 0;
  
  console.log('Analytics disabled');
};

/**
 * Check if analytics is initialized
 */
export const isAnalyticsInitialized = () => isInitialized;
